import axios from 'axios';
import { security, storageKeys } from '../config/environment';

/**
 * Encode une chaîne en Base64
 */
const encodeBase64 = (value) => {
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
        return window.btoa(unescape(encodeURIComponent(value)));
    }
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(value, 'utf8').toString('base64');
    }
    throw new Error('Base64 encoding is not supported in this environment.');
};

/**
 * Calcule le temps d'expiration absolu
 */
const absoluteExpirationTime = (expiresIn) => {
    return Date.now() + (parseInt(expiresIn, 10) * 1000);
};

/**
 * Normalise la session avec les champs nécessaires
 */
const normalizeSession = (data) => {
    if (!data || !data.access_token) {
        throw new Error("La réponse d'authentification est invalide.");
    }

    return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in || 3600, // Par défaut 1 heure
        expires_at: absoluteExpirationTime(data.expires_in || 3600),
        token_type: data.token_type || 'Bearer',
        scope: data.scope || 'read',
        authenticated: true
    };
};

/**
 * Persiste la session dans le localStorage
 */
export const persistSession = (session) => {
    try {
        localStorage.setItem(storageKeys.session, JSON.stringify(session));
    } catch (error) {
        console.error('Erreur lors de la sauvegarde de la session', error);
    }
};

/**
 * Récupère la session depuis le localStorage
 */
export const getSession = () => {
    try {
        const raw = localStorage.getItem(storageKeys.session);
        if (!raw) {
            return null;
        }
        const session = JSON.parse(raw);
        
        // Vérifier si la session est expirée
        if (session.expires_at && session.expires_at < Date.now()) {
            // Si on a un refresh_token, on peut essayer de le rafraîchir
            if (session.refresh_token) {
                // La session expirée sera gérée par le refresh automatique
                return session;
            }
            localStorage.removeItem(storageKeys.session);
            return null;
        }
        
        return session;
    } catch (error) {
        console.error('Impossible de charger la session', error);
        localStorage.removeItem(storageKeys.session);
        return null;
    }
};

/**
 * Supprime la session
 */
export const clearSession = () => {
    try {
        localStorage.removeItem(storageKeys.session);
    } catch (error) {
        console.error('Erreur lors de la suppression de la session', error);
    }
};

/**
 * Authentifie un utilisateur avec username/password (Password Grant)
 */
export const authenticateUser = async (username, password) => {
    const tokenEndpoint = security.serverTokenEndpoint || 
                         process.env.REACT_APP_OAUTH_TOKEN_ENDPOINT || 
                         process.env.EXPO_PUBLIC_OAUTH_TOKEN_ENDPOINT;
    const clientId = security.client_id;
    const clientSecret = security.client_secret;

    if (!tokenEndpoint) {
        throw new Error("L'URL du serveur OAuth est manquante.");
    }

    if (!clientId || !clientSecret) {
        throw new Error("Les identifiants OAuth sont manquants.");
    }

    const payload = new URLSearchParams({
        grant_type: security.grant_type,
        scope: security.scope,
        username,
        password
    });

    const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${encodeBase64(`${clientId}:${clientSecret}`)}`
    };

    try {
        const response = await axios.post(tokenEndpoint, payload, { headers });
        const session = normalizeSession(response.data);
        persistSession(session);
        return session;
    } catch (error) {
        console.error('OAuth Error Details:', {
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url
        });

        const message = error.response?.data?.error_description ||
            error.response?.data?.message ||
            error.message ||
            'Erreur d\'authentification';
        throw new Error(message);
    }
};

/**
 * Rafraîchit le token d'accès en utilisant le refresh_token
 */
export const refreshAccessToken = async (refreshToken) => {
    const tokenEndpoint = security.serverTokenEndpoint || 
                         process.env.REACT_APP_OAUTH_TOKEN_ENDPOINT || 
                         process.env.EXPO_PUBLIC_OAUTH_TOKEN_ENDPOINT;
    const clientId = security.client_id;
    const clientSecret = security.client_secret;

    if (!tokenEndpoint || !refreshToken) {
        throw new Error("Impossible de rafraîchir le token : paramètres manquants.");
    }

    const payload = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        scope: security.scope
    });

    const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${encodeBase64(`${clientId}:${clientSecret}`)}`
    };

    try {
        const response = await axios.post(tokenEndpoint, payload, { headers });
        const session = normalizeSession(response.data);
        persistSession(session);
        return session;
    } catch (error) {
        console.error('Erreur lors du rafraîchissement du token', error);
        // Si le refresh échoue, on supprime la session
        clearSession();
        throw new Error('Impossible de rafraîchir le token. Veuillez vous reconnecter.');
    }
};

/**
 * Révoque un token (logout)
 */
export const revokeToken = async (token, tokenTypeHint = 'access_token') => {
    const revokeEndpoint = security.serverRevokeEndpoint || 
                          process.env.REACT_APP_OAUTH_REVOKE_ENDPOINT || 
                          process.env.EXPO_PUBLIC_OAUTH_REVOKE_ENDPOINT;
    const clientId = security.client_id;
    const clientSecret = security.client_secret;

    if (!revokeEndpoint || !token) {
        console.warn('Endpoint de révocation ou token manquant, suppression locale uniquement');
        clearSession();
        return true;
    }

    const body = new URLSearchParams({
        token,
        token_type_hint: tokenTypeHint,
        client_id: clientId
    });

    const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${encodeBase64(`${clientId}:${clientSecret}`)}`
    };

    try {
        await axios.post(revokeEndpoint, body, { headers });
        clearSession();
        return true;
    } catch (error) {
        console.error('Erreur lors de la révocation du token', error);
        // Même en cas d'erreur, on supprime la session locale
        clearSession();
        return false;
    }
};